import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { getFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import { showNotification } from "aldoo-ra/Notification"
import PlatformProductList from "../../PlatformProduct/components/PlatformProductList"
import { PlatformProductType } from "../../PlatformProduct"
import { SubscriptionPhaseType } from "../../SubscriptionPhase"
import { getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import { priceDisplayFormat } from "../../StorePrice/display-formatters"

/**
 * Compact price model editor with custom layout:
 * - Primary checkbox in upper right
 * - Phases editor on top
 * - Platform Product IDs on bottom
 *
 * This editor bypasses the group system for a clean, compact layout
 */
export default function CompactPriceEditor({
  onChange,
  value,
  field,
  readOnly = false,
  parentValues,
  parentObject,
  fieldValues,
  onSave
}) {
  const [localValue, setLocalValue] = useState(value || fieldValues || {})

  useEffect(() => {
    setLocalValue(value || fieldValues || {})
  }, [value, fieldValues])

  const handleFieldChange = (fieldId, fieldValue) => {
    const updatedValue = { ...localValue, [fieldId]: fieldValue }
    setLocalValue(updatedValue)
    onChange?.(updatedValue)
    onSave?.(updatedValue)
  }

  // Check if phases should be visible (Web platform present)
  const hasWebPlatform = () => {
    const productIds = localValue?.real_product_id
    if (!productIds || !Array.isArray(productIds) || productIds.length === 0) {
      return false
    }

    return productIds.some(product => {
      const platform = typeof product?.platform === 'object'
        ? product.platform.value
        : product?.platform
      return platform?.toLowerCase() === 'web'
    })
  }

  const showPhases = hasWebPlatform()

  // Define field configurations
  const primaryField = {
    id: 'primary',
    type: 'Boolean',
    label: 'Primary Price',
    render: { variant: 'full' }
  }

  const phasesField = {
    id: 'phases',
    type: 'Array',
    label: 'Phases',
    item: SubscriptionPhaseType,
    render: { variant: 'full' },
    fieldEditor: 'PhaseSlotEditor'
  }

  const platformProductsField = {
    id: 'real_product_id',
    type: 'Array',
    label: 'Platform Product IDs',
    item: PlatformProductType,
    render: { variant: 'full' },
    fieldEditor: 'PlatformProductEditor'
  }

  // Get field editor components
  const PrimaryEditor = getFieldEditor(primaryField, localValue)
  const PhasesEditor = getFieldEditor(phasesField, localValue)
  const PlatformProductsEditor = getFieldEditor(platformProductsField, localValue)

  const handleCopyProductId = (productId, platformName) => {
    navigator.clipboard.writeText(productId).then(() => {
      showNotification({
        message: `${platformName} product ID copied to clipboard!`,
        className: 'bg-admin_success text-white'
      })
    }).catch(() => {
      showNotification({
        message: 'Failed to copy product ID',
        className: 'bg-admin_error text-white'
      })
    })
  }

  // Generate dynamic pricing title based on phases
  const generatePricingTitle = () => {
    const phases = localValue?.phases
    if (!phases || !Array.isArray(phases) || phases.length === 0) {
      return null
    }

    // Filter enabled phases and sort by type
    const enabledPhases = phases.filter(p => p.enabled !== false)
    const trial = enabledPhases.find(p => p.phase_type === 'trial')
    const introductory = enabledPhases.find(p => p.phase_type === 'introductory')
    const regular = enabledPhases.find(p => p.phase_type === 'regular')

    if (!regular) return null

    // Format interval (e.g., "week", "month")
    const formatInterval = (phase) => {
      const count = phase.interval_count || 1
      const interval = phase.interval || 'month'
      if (count === 1) {
        return interval
      }
      return `${count} ${interval}s`
    }

    // Format duration (e.g., "3 days", "1 month")
    const formatDuration = (phase) => {
      const iterations = phase.iterations
      const count = phase.interval_count || 1
      const interval = phase.interval || 'month'

      if (!iterations) return null

      const totalUnits = iterations * count
      if (totalUnits === 1) {
        return `${totalUnits} ${interval}`
      }
      return `${totalUnits} ${interval}s`
    }

    // Build title parts
    let titleParts = []

    // Main price - check if free
    if (regular.amount === 0) {
      titleParts.push('Free')
    } else {
      const regularPrice = priceDisplayFormat(regular.amount / 100, regular.currency)
      const regularInterval = formatInterval(regular)
      titleParts.push(`${regularPrice}/${regularInterval}`)
    }

    // Add trial info
    if (trial && trial.amount === 0) {
      const trialDuration = formatDuration(trial)
      if (trialDuration) {
        titleParts.push(`with ${trialDuration} trial`)
      }
    }

    // Add introductory offer info
    if (introductory) {
      const introPrice = priceDisplayFormat(introductory.amount / 100, introductory.currency)
      const introDuration = formatDuration(introductory)
      if (introDuration) {
        titleParts.push(`${introPrice} for the first ${introDuration}`)
      }
    }

    return titleParts.join('. ')
  }

  const pricingTitle = generatePricingTitle()

  return (
    <div className="compact-price-editor">
      {/* 50/50 Layout: Platform IDs (left) | Phases (right) */}
      <div className="flex gap-6">
        {/* Left: Platform Product IDs - Just the list with editor functionality */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <Typography variant="h6" className="text-admin_text font-medium">
              Platform Product IDs
            </Typography>
            <Tooltip
              content="Configure platform-specific product IDs for each store (Google Play, App Store, etc.)"
              position="top"
              display="inline-flex"
            >
              <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_primary cursor-help transition-colors" />
            </Tooltip>
          </div>

          {/* Use the full editor which includes the list and add button */}
          {PlatformProductsEditor && (
            <PlatformProductsEditor
              field={platformProductsField}
              value={localValue.real_product_id}
              onChange={(val) => handleFieldChange('real_product_id', val)}
              parentObject={localValue}
              readOnly={readOnly}
            />
          )}
        </div>

        {/* Right: Phases Section (only if Web platform) */}
        {showPhases && PhasesEditor && (
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Typography variant="h6" className="text-admin_text font-medium">
                Phases
              </Typography>
              <Tooltip
                content="Configure trial, introductory offers, and regular pricing using Stripe Subscription Schedules. Available only for Web platform subscriptions."
                position="top"
                display="inline-flex"
              >
                <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_primary cursor-help transition-colors" />
              </Tooltip>
            </div>

            <PhasesEditor
              field={phasesField}
              value={localValue.phases}
              onChange={(val) => handleFieldChange('phases', val)}
              parentObject={localValue}
              parentValues={parentValues}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
    </div>
  )
}
